// Live's guides on the timeline (issue 828 bug 28; live's `guidelines.ts` and `guideline.ts`): a
// dragged clip's start or end, or a trimmed edge, snaps to the nearest of the other clips' edges
// on every track, the sounds' beats (spec beats.md), the markers (spec markers.md), the playhead
// and 0 when it comes within live's 10 px, and the timeline draws live's guide line there. This
// is what glues two clips together.

import { beatAt, beatTimes, clipMs, type Clip, type Timeline } from "./timeline";

/** Live's snapping distance on screen, at any zoom. */
export const SNAP_PX = 10;

/** Where an edge snapped, or null: the time to draw the guide at. */
export interface Snapped {
  ms: number;
  guideMs: number | null;
}

/**
 * The times an edge may snap to: every other clip's start and end, every other sound's beats that
 * show (spec beats.md), the markers (spec markers.md), the playhead and 0. A group moving together (spec group-moves.md) leaves
 * out all its clips, which move with it, their beats too. A trim (`trimmed`) keeps the trimmed
 * sound's own beats, shown or not: they stay on the music while its edge moves.
 */
export function snapStops(
  timeline: Timeline,
  except: string | readonly string[],
  playheadMs: number,
  trimmed = false,
): number[] {
  const moving = new Set(typeof except === "string" ? [except] : except);
  const stops = new Set([0, Math.round(playheadMs)]);
  for (const clip of timeline.clips) {
    const own = moving.has(clip.id);
    if (!own) {
      stops.add(clip.startMs);
      stops.add(clip.endMs);
    }
    if (clip.kind !== "audio") continue;
    if (!own) for (const atMs of beatTimes(clip)) stops.add(atMs);
    else if (trimmed) for (const beat of clip.beatsMs) stops.add(Math.max(0, beatAt(clip, beat)));
  }
  for (const marker of timeline.markers) stops.add(marker.atMs);
  return [...stops];
}

/**
 * A dragged clip's start, so that its start or its end lies on the nearest stop within
 * `withinMs`; a place `free` refuses (another clip of the track there) is passed over for the
 * next nearest. Unsnapped, it stays where the pointer has it.
 */
export function snapMove(
  stops: readonly number[],
  clip: Clip,
  startMs: number,
  withinMs: number,
  free: (startMs: number) => boolean,
): Snapped {
  const length = clipMs(clip);
  // One pass over the stops, a place kept only within reach: a sound's beats make thousands.
  const near: { start: number; guide: number; by: number }[] = [];
  for (const stop of stops) {
    const byStart = Math.abs(stop - startMs);
    if (byStart < withinMs && stop >= 0) near.push({ start: stop, guide: stop, by: byStart });
    const byEnd = Math.abs(stop - (startMs + length));
    if (byEnd < withinMs && stop - length >= 0) near.push({ start: stop - length, guide: stop, by: byEnd });
  }
  const found = near.sort((one, other) => one.by - other.by).find((each) => free(each.start));
  return found === undefined ? { ms: startMs, guideMs: null } : { ms: found.start, guideMs: found.guide };
}

/** A trimmed edge's time on the nearest stop within `withinMs`, or where the pointer has it. */
export function snapEdge(stops: readonly number[], atMs: number, withinMs: number): Snapped {
  const near = stops
    .filter((stop) => Math.abs(stop - atMs) < withinMs)
    .sort((one, other) => Math.abs(one - atMs) - Math.abs(other - atMs))[0];
  return near === undefined ? { ms: atMs, guideMs: null } : { ms: near, guideMs: near };
}
